// 行业涨跌 / 行业资金 表页面（/industry、/industry/flow）。原先内联在 MarketObservation.jsx。
import React from "react";
import { ArrowDown, ArrowUp, BarChart3, ChevronDown, ChevronRight, Search, TrendingUp, X } from "lucide-react";

import { getMarketIndustries, getMarketIndustryFlow } from "../../api/client";
import { BreadthMini } from "./MarketAtoms";
import { ErrorState, EvidenceFooter, LoadingState, PageHeading, Unavailable } from "./MarketScaffold";
import { integer, number, signed, toneClass } from "./marketFormat";
import { useLatestRequest } from "./useLatestRequest";
import baseStyles from "./marketObservation.module.css";
import overviewStyles from "./marketOverview.module.css";
import tableStyles from "./marketTables.module.css";

const styles = { ...baseStyles, ...overviewStyles, ...tableStyles };

const INDUSTRY_COLUMNS = [
  ["member_count", "成分股"],
  ["pct_chg", "今日涨跌"],
  ["pct_chg_5d", "5日涨跌"],
  ["breadth", "涨跌分布"],
  ["rise", "上涨"],
  ["fall", "下跌"],
  ["turnover_yi", "成交额"],
];

const FLOW_COLUMNS = [
  ["member_count", "成分股"],
  ["main_net_inflow_yi", "主力净流入"],
  ["main_net_inflow_ratio", "净流入占比"],
  ["turnover_yi", "成交额"],
];

export function IndustryTablePage({ mode, navigate, onStatus, snapshotId }) {
  const isFlow = mode === "flow";
  const [query, setQuery] = React.useState("");
  const [sort, setSort] = React.useState(isFlow ? "main_net_inflow_yi" : "pct_chg");
  const [order, setOrder] = React.useState("desc");
  const [level, setLevel] = React.useState("L1");
  const [expanded, setExpanded] = React.useState(new Set());
  const [payload, setPayload] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const requestGuard = useLatestRequest();

  const load = React.useCallback(async () => {
    const requestVersion = requestGuard.start();
    setLoading(true);
    setError("");
    try {
      const data = isFlow
        ? await getMarketIndustryFlow({ snapshotId, level, query, sort, order })
        : await getMarketIndustries({ snapshotId, level: "all", query, sort, order });
      if (!requestGuard.isCurrent(requestVersion)) return;
      setPayload(data);
      onStatus({ status: data.status, dataDate: data.data_date });
    } catch (requestError) {
      if (!requestGuard.isCurrent(requestVersion)) return;
      setError(requestError.message);
      onStatus({ status: "unavailable", dataDate: null });
    } finally {
      if (requestGuard.isCurrent(requestVersion)) setLoading(false);
    }
  }, [isFlow, level, query, sort, order, onStatus, requestGuard, snapshotId]);
  React.useEffect(() => { const timer = window.setTimeout(() => void load(), 180); return () => window.clearTimeout(timer); }, [load]);
  function changeSort(nextSort) {
    if (nextSort === sort) setOrder(order === "desc" ? "asc" : "desc");
    else { setSort(nextSort); setOrder("desc"); }
  }
  function toggleExpanded(code) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }
  const columns = isFlow ? FLOW_COLUMNS : INDUSTRY_COLUMNS;
  const tableColumnClasses = isFlow ? [styles.rankColumn, styles.industryColumn, styles.memberColumn, styles.moneyFlowColumn, styles.flowRatioColumn, styles.turnoverColumn] : [styles.rankColumn, styles.industryColumn, styles.memberColumn, styles.changeColumn, styles.changeColumn, styles.breadthColumn, styles.countColumn, styles.countColumn, styles.turnoverColumn];
  return (
    <div className={styles.page}>
      <PageHeading
        eyebrow="SECTOR BOARD"
        title={isFlow ? "行业资金" : "行业涨跌"}
        description={isFlow ? "按申万行业汇总大单与超大单的真实净流入。" : "查看今日、5日、涨跌分布与成分覆盖。"}
        status={payload?.status ?? (loading ? "partial" : "unavailable")}
        dataDate={payload?.data_date}
        actions={(
          <button className={styles.secondaryButton} onClick={() => navigate(isFlow ? "/industry" : "/industry/flow")}>
            {isFlow ? <TrendingUp size={16} /> : <BarChart3 size={16} />}
            {isFlow ? "看行业涨跌" : "看资金流向"}
          </button>
        )}
      />
      <section className={styles.tableCard}>
        <div className={styles.tableToolbar}>
          <label className={styles.searchBox}>
            <Search size={16} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索行业名称或代码" />
            {query && <button aria-label="清空搜索" onClick={() => setQuery("")}><X size={15} /></button>}
          </label>
          {isFlow && (
            <div className={styles.segmented}>
              {[["L1", "一级行业"], ["L2", "二级行业"]].map(([value, label]) => (
                <button key={value} className={level === value ? styles.active : ""} aria-pressed={level === value} onClick={() => setLevel(value)}>{label}</button>
              ))}
            </div>
          )}
          <span className={styles.resultCount}>{payload ? `${payload.total_l1 ?? payload.items?.length ?? 0} 个${level === "L2" ? "二级" : "一级"}行业` : ""}</span>
        </div>
        {loading && !payload ? <LoadingState label="正在汇总行业数据" /> : error ? <ErrorState error={error} onRetry={load} /> : (
          <div className={styles.tableScroll}>
            {payload?.status === "partial" && (
              <Unavailable reason={isFlow ? "行业成分尚未同步：资金流保持缺失状态，不按 0 计算" : "部分行业缺少官方指数或成分关系；缺失指标以 -- 展示"} />
            )}
            <table className={styles.industryTable}>
              <colgroup>{tableColumnClasses.map((className, index) => <col key={`${className}-${index}`} className={className} />)}</colgroup>
              <thead>
                <tr>
                  <th>排名</th>
                  <th>行业</th>
                  {columns.map(([key, label]) => key === "breadth" ? <th key={key}>{label}</th> : <th key={key} aria-sort={sort === key ? (order === "asc" ? "ascending" : "descending") : "none"}><button onClick={() => changeSort(key)}>{label}{sort === key && (order === "desc" ? <ArrowDown size={13} /> : <ArrowUp size={13} />)}</button></th>)}
                </tr>
              </thead>
              <tbody>
                {(payload?.items ?? []).map((item, index) => (
                  <React.Fragment key={item.industry_code}>
                    <IndustryRow
                      item={item}
                      rank={index + 1}
                      isFlow={isFlow}
                      expandable={!isFlow && !!item.children?.length}
                      expanded={expanded.has(item.industry_code) || Boolean(query)}
                      onToggle={() => toggleExpanded(item.industry_code)}
                      onOpen={() => navigate(`/industry/${item.industry_code}`)}
                    />
                    {!isFlow && (expanded.has(item.industry_code) || Boolean(query)) && item.children?.map((child) => (
                      <IndustryRow key={child.industry_code} item={child} isChild onOpen={() => navigate(`/industry/${child.industry_code}`)} />
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {!payload?.items?.length && <Unavailable reason="没有匹配的行业" />}
          </div>
        )}
      </section>
      {payload && <EvidenceFooter payload={payload} />}
    </div>
  );
}

function IndustryRow({ item, rank, isFlow = false, isChild = false, expandable = false, expanded = false, onToggle, onOpen }) {
  return (
    <tr className={isChild ? styles.childRow : ""}>
      <td>{isChild ? "" : rank}</td>
      <td>
        <div className={styles.industryNameCell}>
          {expandable ? (
            <button className={styles.expandButton} aria-label={expanded ? "收起二级行业" : "展开二级行业"} aria-expanded={expanded} onClick={onToggle}>
              {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
          ) : <span className={styles.expandSpacer} />}
          <button className={styles.industryLink} onClick={onOpen}>
            <strong>{item.industry_name}</strong><small>{item.industry_code}</small>
          </button>
        </div>
      </td>
      <td>{integer(item.member_count)}</td>
      {isFlow ? (
        <>
          <td className={toneClass(item.main_net_inflow_yi, styles)}>{signed(item.main_net_inflow_yi, " 亿", 2)}</td>
          <td className={toneClass(item.main_net_inflow_ratio, styles)}>{item.main_net_inflow_ratio === null || item.main_net_inflow_ratio === undefined ? "--" : signed(Number(item.main_net_inflow_ratio) * 100)}</td>
          <td>{item.turnover_yi === null || item.turnover_yi === undefined ? "--" : `${number(item.turnover_yi, 1)} 亿`}</td>
        </>
      ) : (
        <>
          <td className={toneClass(item.pct_chg, styles)}>{signed(item.pct_chg)}</td>
          <td className={toneClass(item.pct_chg_5d, styles)}>{signed(item.pct_chg_5d)}</td>
          <td><BreadthMini item={item} styles={styles} /></td>
          <td className={styles.up}>{integer(item.rise)}</td>
          <td className={styles.down}>{integer(item.fall)}</td>
          <td>{item.turnover_yi === null || item.turnover_yi === undefined ? "--" : `${number(item.turnover_yi, 1)} 亿`}</td>
        </>
      )}
    </tr>
  );
}
